import React from "react";
import { useTranslation } from "react-i18next";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn, renderDateStringForDateFormField } from "@/lib/utils";
import { Control } from "react-hook-form";
import { parse, isValid } from "date-fns";

interface DateFormFieldProps {
  control: Control<any>;
  name: string;
  label?: string;
  description?: string;
  required?: boolean;
  showClearButton?: boolean;
  fromYear?: number;
  toYear?: number;
  className?: string;
  showInputField?: boolean;
  dateFormat?: string;
}

export function DateFormField({
  control,
  name,
  label,
  description,
  required = false,
  showClearButton = true,
  fromYear = 1960,
  toYear = 2030,
  className,
  showInputField = true,
  dateFormat = "MM/dd/yyyy",
}: DateFormFieldProps) {
  const { t } = useTranslation();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn("flex flex-col", className)}>
          <FormLabel>
            {required && "* "}
            {label || t("common.label.date")}
          </FormLabel>
          <div className="flex gap-2">
            {showInputField && (
              <FormControl>
                <Input
                  type="text"
                  placeholder={dateFormat}
                  value={
                    field.value
                      ? renderDateStringForDateFormField(
                          field.value,
                          dateFormat,
                        )
                      : ""
                  }
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    field.onChange(inputValue);
                  }}
                  onBlur={() => {
                    const parsedDate = parse(
                      field.value,
                      dateFormat,
                      new Date(),
                    );
                    if (field.value && isValid(parsedDate)) {
                      field.onChange(parsedDate);
                    }
                  }}
                  className="w-[240px] [&::-webkit-calendar-picker-indicator]:hidden"
                />
              </FormControl>
            )}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant={"outline"}
                  className={cn(
                    "pl-3 text-left font-normal",
                    !field.value && "text-muted-foreground",
                  )}
                >
                  <CalendarIcon className="h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <div className="flex flex-col">
                  <Calendar
                    mode="single"
                    captionLayout="dropdown-buttons"
                    fromYear={fromYear}
                    toYear={toYear}
                    onSelect={field.onChange}
                    selected={field.value || undefined}
                    initialFocus
                  />
                  {showClearButton && field.value && (
                    <Button
                      variant="ghost"
                      className="rounded-none border-t"
                      onClick={() => {
                        field.onChange(null);
                      }}
                    >
                      {t("common.button.clear")}
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>
          {description && (
            <FormDescription>
              <div className="flex flex-wrap gap-2 items-center justify-start">
                <p>{description}</p>
                {showClearButton && field.value && (
                  <Button
                    type="button"
                    className="w-auto p-0 m-0 h-auto"
                    variant="link"
                    onClick={() => {
                      field.onChange(null);
                    }}
                  >
                    {t("common.button.clear")}
                  </Button>
                )}
              </div>
            </FormDescription>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
